// @ts-strict-ignore
import { Fragment, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { List } from '@mui/material';
import { useFragment, graphql } from 'react-relay';
import { Paragraph } from '../Paragraph';
import { H5 } from '../H5';
import RespondentDeleteDialog from './DeleteRecipientDialog';
import Recipient from './Recipient';
import type { RecipientProps } from './Recipient';
import RemoveRecipientsFromListMutation from './RemoveRecipientsFromListMutation';
import type { RecipientList_recipientList$key } from './__generated__/RecipientList_recipientList.graphql';

export interface RecipientListProps {
    classes: Record<'RespondentList' | 'list', string> &
        RecipientProps['classes'];
    recipientList: RecipientList_recipientList$key;
}

function RecipientList(props: RecipientListProps) {
    const { classes } = props;
    const recipientList = useFragment(
        fragments.recipientList,
        props.recipientList,
    );
    const { id: recipientListId, recipients } = recipientList;

    const [deleteRecipientDialogState, setDeleteRecipientDialogState] =
        useState<{
            open: boolean;
            recipient?: (typeof recipientList)['recipients'][number];
            deleting: boolean;
            errorMessage: string;
        }>({
            open: false,
            deleting: false,
            errorMessage: '',
        });

    const showDeleteRecipientDialog = useCallback((recipient) => {
        setDeleteRecipientDialogState({
            open: true,
            recipient,
            deleting: false,
            errorMessage: '',
        });
    }, []);

    const onDeleteRecipient = async () => {
        const { phoneNumber, emailAddress } =
            deleteRecipientDialogState.recipient;
        const input = {
            recipientListId,
            emailAddresses: emailAddress ? [emailAddress] : [],
            phoneNumbers: phoneNumber ? [phoneNumber] : [],
        };
        try {
            setDeleteRecipientDialogState({
                ...deleteRecipientDialogState,
                deleting: true,
                errorMessage: '',
            });
            await RemoveRecipientsFromListMutation(input);
            setDeleteRecipientDialogState({
                ...deleteRecipientDialogState,
                open: false,
            });
        } catch {
            setDeleteRecipientDialogState({
                ...deleteRecipientDialogState,
                deleting: false,
                errorMessage: 'Failed removing recipient',
            });
        }
    };

    const respondentCount = recipients.length;

    const emailRecipients = recipients.filter(({ type }) => type === 'EMAIL');
    const smsRecipients = recipients.filter(({ type }) => type === 'SMS');

    return (
        <div className={classes.RespondentList}>
            <RespondentDeleteDialog
                {...deleteRecipientDialogState}
                onClose={() =>
                    setDeleteRecipientDialogState({
                        ...deleteRecipientDialogState,
                        open: false,
                    })
                }
                onDelete={onDeleteRecipient}
            />

            {respondentCount === 0 && (
                <div>
                    <Paragraph>
                        <FormattedMessage defaultMessage="No respondents yet" />
                    </Paragraph>
                </div>
            )}
            {emailRecipients.length > 0 && (
                <Fragment>
                    <H5>
                        <FormattedMessage
                            defaultMessage="Email addresses ({count})"
                            values={{ count: `${emailRecipients.length}` }}
                        />
                    </H5>
                    <List className={classes.list}>
                        {emailRecipients.map((recipient) => (
                            <Recipient
                                key={recipient.emailAddress}
                                recipient={recipient}
                                classes={classes}
                                onClickDelete={showDeleteRecipientDialog}
                            />
                        ))}
                    </List>
                </Fragment>
            )}
            {smsRecipients.length > 0 && (
                <Fragment>
                    <H5>
                        <FormattedMessage
                            defaultMessage="Phone Numbers ({ count })"
                            values={{ count: `${smsRecipients.length}` }}
                        />
                        {/*respondentPhoneNumbers.length > 0 ? <a onClick={ this._showDeletePhone } key={'editEmail'} className={classes.link}>[edit]</a>: null */}
                    </H5>
                    <List className={classes.list}>
                        {smsRecipients.map((recipient) => (
                            <Recipient
                                key={recipient.phoneNumber}
                                recipient={recipient}
                                classes={classes}
                                onClickDelete={showDeleteRecipientDialog}
                            />
                        ))}
                    </List>
                </Fragment>
            )}
        </div>
    );
}

const fragments = {
    recipientList: graphql`
        fragment RecipientList_recipientList on RecipientList {
            id
            recipients {
                type
                emailAddress
                phoneNumber
                respondentId
                preRegisteredMetaQuestionResponses {
                    questionId: originalQuestionId
                    selectedResponseOptions {
                        value
                    }
                }
            }
        }
    `,
};

export default RecipientList;
